import { useEffect, useState, useRef } from 'react';

// mui
import { Typography, Box, Chip, Stack, IconButton, Tooltip } from '@mui/material';

// custom icon
import { InstagramCommentIcon } from '../asset/svg.jsx';

// icons
import { FavoriteBorder as FavoriteBorderIcon, Favorite as FavoriteIcon } from '@mui/icons-material';

// client-shared
import { MarkDown } from '@goldblack/client-comps';
import { number } from '@goldblack/client-utils';
import { def } from '@goldblack/shared';

const TAG_TOOLTIPS = {
    AI: <>This post was created using AI.</>,
    Sponsored: <>This post is sponsored by a brand or advertiser.</>,
};

function PostShowMediaBottom(props) {
    const [mode, setMode] = useState('collapse');
    const [textColor, setTextColor] = useState('');
    const [height, setHeight] = useState('');
    const ref = useRef(null);
    const dragInfo = useRef({ startX: 0, startY: 0, moved: false });

    const onClick = () => {
        if (!dragInfo.current.moved) {
            setMode(mode === 'expanded' ? 'collapse' : 'expanded');
        }
    };

    const [pop, setPop] = useState(null); // heart animation right after a click: 'like' | 'unlike'

    const onClickLike = (e) => {
        e.stopPropagation();
        setPop(props.post.liked ? 'unlike' : 'like');
        props.onClickLike();
    };

    const onClickComment = (e) => {
        e.stopPropagation();
        props.onClickComment();
    };

    const handleMouseDown = (e) => {
        dragInfo.current = {
            startX: e.clientX,
            startY: e.clientY,
            moved: false,
        };
    };

    const handleMouseUp = (e) => {
        const dx = Math.abs(e.clientX - dragInfo.current.startX);
        const dy = Math.abs(e.clientY - dragInfo.current.startY);
        if (dx > 5 || dy > 5) {
            dragInfo.current.moved = true; // 드래그로 간주
        }
    };

    const handleWheel = (e) => {
        const box = ref.current;
        if (!box) {
            return;
        }

        if (mode === 'expanded') {
            // ✅ expanded 모드면 무조건 안쪽에서 휠 먹고 부모 막기
            e.stopPropagation();
            return;
        }

        // ✅ collapse 모드일 때는 조건부로
        const { scrollTop, scrollHeight, clientHeight } = box;
        const isScrollingDown = e.deltaY > 0;

        if ((isScrollingDown && scrollTop + clientHeight < scrollHeight) || (!isScrollingDown && scrollTop > 0)) {
            e.stopPropagation();
        }
    };

    useEffect(() => {
        const isLong = props.post.desc ? props.post.desc.indexOf('\n') !== -1 || props.post.desc.length > 100 : false;
        setTextColor(mode === 'collapse' ? 'text.primary' : 'primary.main');
        setHeight(mode === 'collapse' && isLong ? '60px' : 'auto');
    }, [mode, props.post.desc]);

    const status = props.post?.status;
    if (status === def.PostStatus.BLOCKED || status === def.PostStatus.CENSORED || status === def.PostStatus.PROCESSING)
        return null;

    return (
        <Box
            ref={ref}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onWheel={handleWheel}
            onClick={onClick}
            sx={{
                width: '100%',
                height: height,
                maxHeight: '50%',
                position: 'absolute',
                bottom: '0%',
                left: '0%',
                transform: 'translate(0%, 0%)',
                background: 'linear-gradient( #00000000, #0000007f)',
                overflow: mode === 'collapse' ? 'hidden' : 'auto',
                userSelect: 'text',
                '&::-webkit-scrollbar': {
                    width: '0px', // 스크롤바 트랙 자체를 없애버림
                    background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: 'transparent',
                },
            }}>
            <Stack direction='row' justifyContent='flex-start' alignItems='center'>
                <Stack direction='row' justifyContent='center' alignItems='center' sx={{ mx: 1 }}>
                    <Tooltip title={props.post.liked ? 'unlike' : 'like'}>
                        <IconButton
                            aria-label={props.post.liked ? 'unlike' : 'like'}
                            sx={{ m: 0, p: 0 }}
                            onClick={onClickLike}>
                            {props.post.liked ? (
                                <FavoriteIcon
                                    onAnimationEnd={() => setPop(null)}
                                    sx={{
                                        m: 0,
                                        fontSize: '28px',
                                        color: 'white',
                                        mr: 0.5,
                                        '@keyframes heartPop': {
                                            '0%': { transform: 'scale(1)' },
                                            '40%': { transform: 'scale(1.35)' },
                                            '100%': { transform: 'scale(1)' },
                                        },
                                        ...(pop === 'like' && { animation: 'heartPop 0.35s ease-out' }),
                                    }}
                                />
                            ) : (
                                <FavoriteBorderIcon
                                    onAnimationEnd={() => setPop(null)}
                                    sx={{
                                        m: 0,
                                        fontSize: '28px',
                                        color: 'white',
                                        mr: 0.5,
                                        '@keyframes heartShrink': {
                                            '0%': { transform: 'scale(1)' },
                                            '40%': { transform: 'scale(0.7)' },
                                            '100%': { transform: 'scale(1)' },
                                        },
                                        ...(pop === 'unlike' && { animation: 'heartShrink 0.35s ease-out' }),
                                    }}
                                />
                            )}
                        </IconButton>
                    </Tooltip>
                    <Typography
                        sx={{
                            m: 0,
                            fontSize: '12px',
                            lineHeight: '1',
                            marginTop: '0',
                            color: 'white',
                        }}>
                        {number.formatNumber(props.post.likes, 1)}
                    </Typography>
                </Stack>
                <Stack direction='row' justifyContent='center' alignItems='center' sx={{ mx: 1 }}>
                    <Tooltip title='comment'>
                        <IconButton aria-label='comment' sx={{ m: 0, p: 0 }} onClick={onClickComment}>
                            <InstagramCommentIcon sx={{ m: 0, fontSize: '28px', color: 'white', mr: 0.5 }} />
                        </IconButton>
                    </Tooltip>
                    <Typography
                        sx={{
                            m: 0,
                            fontSize: '12px',
                            lineHeight: '1',
                            marginTop: '0',
                            color: 'white',
                        }}>
                        {number.formatNumber(props.post.comments, 1)}
                    </Typography>
                </Stack>
                {props.post.tags?.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {props.post.tags.map((tag) => (
                            <Tooltip key={tag} title={TAG_TOOLTIPS[tag] ?? tag} placement='bottom'>
                                <Chip
                                    label={tag}
                                    size='small'
                                    color='primary'
                                    variant='outlined'
                                    sx={{ fontSize: '11px', height: '18px', borderWidth: '1.5px' }}
                                />
                            </Tooltip>
                        ))}
                    </Box>
                )}
            </Stack>
            <MarkDown color={textColor} src={props.post.desc} />
        </Box>
    );
}

export { PostShowMediaBottom };
